import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Activity, RefreshCw, Sun, Moon, User, LogOut, Menu, TrendingUp, Gauge, Zap, Calendar, Clock } from 'lucide-react';
import { useTheme } from '../../contexts/theme-context';
import { useAuth } from '../../contexts/auth-context';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
const Navigation = ({
  onMenuClick
}) => {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    logout
  } = useAuth();
  const {
    toast
  } = useToast();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [plantInfo, setPlantInfo] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataAcquisitionHalted, setDataAcquisitionHalted] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    fetchNavigationData();
  }, []);
  const fetchNavigationData = async () => {
    try {
      setIsLoading(true);
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const startDate = yesterday.toISOString().split('T')[0] + ' 00:00:00';
      const endDate = yesterday.toISOString().split('T')[0] + ' 23:59:59';
      console.log('Navigation: Fetching data with params:', {
        startDate,
        endDate,
        plant_id: API_CONFIG.PLANT_ID
      });

      // Fetch plant info and KPI data
      const response = await apiService.getForGraph({
        start_date: startDate,
        end_date: endDate,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });
      console.log('Navigation: API Response:', response);
      const graphArray = Array.isArray(response.data) ? response.data : response.data?.body || [];
      if (graphArray.length > 0 && graphArray[0].readings && graphArray[0].readings.length > 0) {
        const plantData = {
          displayName: "Rexon Power Solar Plant",
          locality: "Mumbai, Maharashtra",
          dcCapacityActual: 250000,
          // 250 MWp
          acCapacityActual: 200000,
          // 200 MW
          pnomRatio: 0.8,
          mount_tech: "Fixed Tilt",
          commissionDate: "2023-01-15",
          plantAgeInDays: 570
        };
        setPlantInfo(plantData);

        // Extract KPI data
        const kpiReading = graphArray[0].readings[0].KPI;
        console.log('Navigation: KPI Reading:', kpiReading);
        if (kpiReading && kpiReading.length > 0) {
          const performanceRatio = kpiReading.find(kpi => kpi.performance_ratio);
          const plantAvailability = kpiReading.find(kpi => kpi.plant_availability);
          const energyExport = kpiReading.find(kpi => kpi.energy_export);
          const energyImport = kpiReading.find(kpi => kpi.energy_import);
          console.log('Navigation: Found KPIs:', {
            performanceRatio,
            plantAvailability,
            energyExport,
            energyImport
          });
          const kpiData = {
            performance_ratio: {
              value: performanceRatio?.performance_ratio || 0,
              unit: '%'
            },
            plant_availability: {
              value: plantAvailability?.plant_availability || 0,
              unit: '%'
            },
            energy_export: {
              value: energyExport?.energy_export || 0,
              unit: 'kWh'
            },
            energy_import: {
              value: energyImport?.energy_import || 0,
              unit: 'kWh'
            }
          };
          setKpiData(kpiData);
        }
      }
    } catch (error) {
      console.error('Error fetching navigation data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch plant data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const formatNumber = num => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2
    }).format(num);
  };
  const formatDate = date => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const formatTime = date => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  const handleLogout = () => {
    logout();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out."
    });
  };
  return <nav className="sticky top-0 z-40 bg-gradient-to-r from-white/80 to-white/60 dark:from-gray-900/70 dark:to-gray-900/50 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-gray-200/80 dark:border-gray-800">
      <div className="px-3 sm:px-4 md:px-6 py-2 sm:py-3">
        {/* Top Bar */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Hamburger for mobile */}
            <button className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300" aria-label="Open menu" onClick={onMenuClick}>
              <Menu className="h-5 w-5" />
            </button>

            {/* Brand */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 via-red-500 to-blue-500 rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-xs">RP</span>
              </div>
              <div className="hidden sm:block">
                <div className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">REXON POWER</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[160px]">
                  {plantInfo?.displayName || 'Rexon Power Solar Plant'}
                </div>
              </div>
            </div>

            {/* Live */}
            <div className="hidden sm:flex items-center space-x-2 bg-green-50 dark:bg-green-900/20 px-2.5 py-1 rounded-full border border-green-200/60 dark:border-green-800">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">LIVE</span>
            </div>
            <Button variant="outline" size="sm" onClick={fetchNavigationData} disabled={isLoading} className="whitespace-nowrap">
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Dark Mode Toggle */}
            <Button variant="outline" size="sm" onClick={toggleTheme} className="flex items-center space-x-2">
              {theme === 'dark' ? <>
                  <Sun className="h-4 w-4" />
                  <span>Light</span>
                </> : <>
                  <Moon className="h-4 w-4" />
                  <span>Dark</span>
                </>}
            </Button>
            
            {/* User Menu */}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Metric pills scroller */}
        <div className="-mx-3 px-3 mt-2 pb-1 overflow-x-auto">
          <div className="flex gap-2.5 sm:gap-3 items-stretch min-w-max">
            {/* PR */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">PR</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{kpiData?.performance_ratio ? `${formatNumber(kpiData.performance_ratio.value)}%` : '—'}</div>
              </div>
            </div>
            {/* Availability */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Availability</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{kpiData?.plant_availability ? `${formatNumber(kpiData.plant_availability.value)}%` : '—'}</div>
              </div>
            </div>
            {/* Export */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Export</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{kpiData?.energy_export ? `${formatNumber(kpiData.energy_export.value)} kWh` : '—'}</div>
              </div>
            </div>
            {/* Import */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Zap className="h-4 w-4 rotate-180 text-purple-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Import</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{kpiData?.energy_import ? `${formatNumber(kpiData.energy_import.value)} kWh` : '—'}</div>
              </div>
            </div>
            {/* DC/AC */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Gauge className="h-4 w-4 text-sky-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">DC/AC</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{plantInfo ? `${formatNumber(plantInfo.dcCapacityActual / 1000)}/${formatNumber(plantInfo.acCapacityActual / 1000)} MW` : '—'}</div>
              </div>
            </div>
            {/* Ratio */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Gauge className="h-4 w-4 text-indigo-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Ratio</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{plantInfo ? `${formatNumber(plantInfo.pnomRatio)}` : '—'}</div>
              </div>
            </div>
            {/* Mount */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-teal-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Mount</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{plantInfo?.mount_tech || '—'}</div>
              </div>
            </div>
            {/* Date */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-rose-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Date</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{formatDate(currentTime)}</div>
              </div>
            </div>
            {/* Time */}
            <div className="bg-white/80 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-700 rounded-xl px-3 py-2 shadow-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-fuchsia-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Time</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{formatTime(currentTime)} GMT+5:30</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>;
};
export default Navigation;